import api from '../../api';
import {getDateRangesApiModel, userIdPath} from '../../helpers/apiHelper';
import {
    availabilityResponse,
    type AvailabilityResponse,
} from '../../models/booking/bookingDateAvailability';
import {
    resourceUsageListModel,
    type ResourceUsageListModel,
    resourceUsageListStreamModel,
    resourceUsageModel,
    type ResourceUsageModel,
} from '../../models/booking/bookingModel';
import {
    resource,
    type Resource,
    resourceDetails,
    type ResourceDetails,
    resourceType,
    type ResourceType,
    type ResourceTypeFilter,
} from '../../models/booking/resourceModel';
import {resourceLiveStatus, type ResourceLiveStatus} from '../../models/booking/resourceLiveStatus';
import type {DateRange} from '../../models/dateTime/dateRange';
import {
    mercureSubscription,
    type MercureSubscription,
} from '../../models/mercure/mercureSubscription';
import {parseZodSchema} from '../../models/parseZodSchema';
import {useLocaleStore} from '@/stores/localeStore.ts';
import {useAuthorizationStore} from '@/stores/authorizationStore';
import {appConfig} from '@/config/appConfig.ts';
import {experimental_streamedQuery as streamedQuery, queryOptions} from '@tanstack/vue-query';

export async function getResourceTypes(): Promise<ResourceType[]> {
    return api
        .get('/api/resource-type/list')
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, resourceType.array()));
}

export async function getMapResources(
    roomId?: string | null,
    userId?: string
): Promise<Resource[]> {
    return api
        .get(`/api/resource/list/${roomId}${userIdPath(userId)}`)
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, resource.array()));
}

export async function getListResources(
    spaceId: string | null,
    dateRanges: DateRange[],
    resourceTypeFilter: ResourceTypeFilter[] | null,
    userId?: string,
    showOnlyBookable: boolean = false,
    showOnlyFavorites: boolean = false
): Promise<ResourceUsageListModel[]> {
    const request = {
        filter: {
            showOnlyBookable,
            showOnlyFavorites,
            resourceTypeFilter,
        },
        ...getDateRangesApiModel(dateRanges),
    };

    return api
        .postJSON(
            `/api/resource/usage/list/${spaceId}${userIdPath(userId)}`,
            Object.assign(request)
        )
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, resourceUsageListModel.array()));
}

export function createResourceUsageListStreamQuery(
    spaceId: string | null,
    dateRanges: DateRange[],
    resourceTypeFilter: ResourceTypeFilter[] | null,
    userId?: string,
    showOnlyBookable: boolean = false,
    showOnlyFavorites: boolean = false
) {
    const request = {
        filter: {
            showOnlyBookable,
            showOnlyFavorites,
            resourceTypeFilter,
        },
        ...getDateRangesApiModel(dateRanges),
    };

    return queryOptions({
        queryKey: [
            'resourceUsageListStream',
            spaceId,
            dateRanges,
            resourceTypeFilter,
            userId,
            showOnlyBookable,
            showOnlyFavorites,
        ],
        queryFn: streamedQuery({
            streamFn: async (): Promise<AsyncIterable<ResourceUsageListModel>> => {
                const {currentLocale} = useLocaleStore();
                const {authorizationHeaders} = useAuthorizationStore();

                const response = await fetch(
                    `/${currentLocale}/api/resource/usage/list-streamed/${spaceId}${userIdPath(userId)}`,
                    {
                        method: 'POST',
                        headers: {
                            ...appConfig.defaultApiRequestHeaders,
                            ...authorizationHeaders,
                            'Content-Type': 'application/json',
                            Accept: 'application/json',
                        } as HeadersInit,
                        body: JSON.stringify(request),
                    }
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                if (!response.body) {
                    throw new Error('ReadableStream not supported in this browser');
                }

                const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
                let buffer = '';

                return {
                    async *[Symbol.asyncIterator]() {
                        try {
                            /* eslint-disable no-console */
                            console.debug('Starting TanStack Query NDJSON stream processing...');

                            while (true) {
                                const {done, value} = await reader.read();

                                if (done) {
                                    // Process any remaining buffer
                                    if (buffer.trim()) {
                                        try {
                                            console.debug(
                                                'Processing final buffer:',
                                                buffer.trim()
                                            );
                                            const streamItem = parseZodSchema(
                                                JSON.parse(buffer.trim()),
                                                resourceUsageListStreamModel
                                            );
                                            const regularItem: ResourceUsageListModel = {
                                                resourceType: streamItem.resourceType,
                                                totalCount: streamItem.totalCount,
                                                resourceUsages: streamItem.resourceUsages,
                                            };
                                            yield regularItem;
                                        } catch (e) {
                                            console.warn('Failed to parse final buffer:', e);
                                        }
                                    }
                                    break;
                                }

                                // Add new chunk to buffer
                                buffer += value;
                                console.debug('Received chunk, buffer length:', buffer.length);

                                // Process complete lines (NDJSON format)
                                const lines = buffer.split('\n');
                                buffer = lines.pop() || ''; // Keep incomplete line in buffer

                                for (const line of lines) {
                                    const trimmedLine = line.trim();
                                    if (trimmedLine) {
                                        try {
                                            console.debug('Processing NDJSON line:', trimmedLine);
                                            const parsed = JSON.parse(trimmedLine);
                                            console.debug('Parsed NDJSON object:', parsed);

                                            // Try stream model first, then regular model
                                            try {
                                                const streamItem = parseZodSchema(
                                                    parsed,
                                                    resourceUsageListStreamModel
                                                );
                                                const regularItem: ResourceUsageListModel = {
                                                    resourceType: streamItem.resourceType,
                                                    totalCount: streamItem.totalCount,
                                                    resourceUsages: streamItem.resourceUsages,
                                                };
                                                yield regularItem;
                                                console.debug(
                                                    'Successfully processed and yielded item for resourceType:',
                                                    regularItem.resourceType
                                                );
                                            } catch (zodError) {
                                                console.warn(
                                                    'Stream validation failed, trying regular model:',
                                                    zodError
                                                );
                                                const regularItem = parseZodSchema(
                                                    parsed,
                                                    resourceUsageListModel
                                                );
                                                yield regularItem;
                                            }
                                        } catch (e) {
                                            console.warn(
                                                'Failed to parse NDJSON line:',
                                                trimmedLine,
                                                e
                                            );
                                        }
                                    }
                                }
                            }
                        } catch (error) {
                            console.error('Stream processing error:', error);
                            /* eslint-enable no-console */
                            throw error;
                        } finally {
                            reader.releaseLock();
                        }
                    },
                };
            },
        }),
        enabled: !!(spaceId && dateRanges.length && resourceTypeFilter?.length),
    });
}

export function getBookingState(
    resourceId: string,
    dateRanges: readonly DateRange[],
    userId?: string
): Promise<AvailabilityResponse> {
    return api
        .postJSON(
            `/api/resource/availability/${resourceId}${userIdPath(userId)}`,
            getDateRangesApiModel(dateRanges)
        )
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, availabilityResponse));
}

export async function getResourceUsages(
    roomId: string,
    dateRanges: readonly DateRange[],
    userId?: string
): Promise<ResourceUsageModel[]> {
    return api
        .postJSON(
            `/api/resource/room/usage/list/${roomId}${userIdPath(userId)}`,
            getDateRangesApiModel(dateRanges)
        )
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, resourceUsageModel.array()));
}

export async function getResourceDetails(resourceId: string): Promise<ResourceDetails> {
    return api
        .get(`/api/resource/${resourceId}`)
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, resourceDetails));
}

export async function getRoomResourcesLiveStatus(roomId: string): Promise<ResourceLiveStatus[]> {
    return api
        .get(`/api/resource/room/live/status/${roomId}`)
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, resourceLiveStatus.array()));
}

export async function getRoomLiveStatusSubscription(
    roomId: string
): Promise<MercureSubscription | null> {
    return api
        .get(`/api/resource/room/live/status/${roomId}/subscribe`)
        .then(api.handle)
        .then(raw => parseZodSchema(raw, mercureSubscription))
        .catch(() => null); // Silent failure - graceful degradation to polling
}
